(() => {
  const SECTION_SELECTOR = '[data-sustainability-impact-section]';
  const VALUE_SELECTOR = '.js-sustainability-impact-value';
  const PARALLAX_BG_SELECTOR = '.section-sustainability-impact__parallax-bg';
  const STATS_CONTAINER_SELECTOR = '.section-sustainability-impact__stats';
  const STAT_CARD_SELECTOR = '.section-sustainability-impact__stat';
  const BUTTONS_CONTAINER_SELECTOR = '.section-sustainability-impact__buttons';
  const BUTTON_SELECTOR = 'a.button';
  const parallaxTeardownMap = new WeakMap();
  const isAboutUsPage = window.location.pathname.toLowerCase().includes('/pages/about-us');

  function teardownParallax(sectionRoot) {
    if (!sectionRoot) return;

    const teardown = parallaxTeardownMap.get(sectionRoot);
    if (typeof teardown === 'function') {
      teardown();
      parallaxTeardownMap.delete(sectionRoot);
    }
  }

  function initParallax(sectionRoot) {
    if (!sectionRoot) return;

    teardownParallax(sectionRoot);

    const bgImage = sectionRoot.querySelector(PARALLAX_BG_SELECTOR);
    if (!bgImage) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      bgImage.style.transform = 'translateY(0)';
      return;
    }

    const initGsapParallax = () => {
      if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
        return false;
      }

      const fromY = isAboutUsPage ? -6 : -10;
      const toY = isAboutUsPage ? 6 : 10;
      const scrubValue = isAboutUsPage ? 0.6 : 1;

      const tween = gsap.fromTo(
        bgImage,
        { yPercent: fromY },
        {
          yPercent: toY,
          ease: 'none',
          force3D: true,
          scrollTrigger: {
            trigger: sectionRoot,
            start: 'top bottom',
            end: 'bottom top',
            scrub: scrubValue,
            invalidateOnRefresh: true,
            onEnter: () => {
              bgImage.style.willChange = 'transform';
            },
            onEnterBack: () => {
              bgImage.style.willChange = 'transform';
            },
            onLeave: () => {
              bgImage.style.willChange = 'auto';
            },
            onLeaveBack: () => {
              bgImage.style.willChange = 'auto';
            },
          },
        }
      );

      parallaxTeardownMap.set(sectionRoot, () => {
        if (tween && tween.scrollTrigger) tween.scrollTrigger.kill();
        if (tween) tween.kill();
        bgImage.style.willChange = 'auto';
        bgImage.style.transform = 'translateY(0)';
      });

      return true;
    };

    if (initGsapParallax()) return;

    const onGsapReady = () => {
      teardownParallax(sectionRoot);
      initParallax(sectionRoot);
    };
    document.addEventListener('theme:gsap:ready', onGsapReady, { once: true });
  }

  function parseTarget(rawValue) {
    const normalized = String(rawValue || '0').replace(/,/g, '').trim();
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function inferPrecision(rawValue) {
    const normalized = String(rawValue || '0').replace(/,/g, '').trim();
    const match = normalized.match(/\.(\d+)/);
    return match ? match[1].length : 0;
  }

  function formatValue(value, precision, digitCount) {
    const safeValue = Number.isFinite(value) ? value : 0;
    const safeDigitCount = Math.max(Number.isInteger(digitCount) ? digitCount : 1, 1);
    const sign = safeValue < 0 ? '-' : '';
    const abs = Math.abs(safeValue);

    if (precision > 0) {
      const fixed = abs.toFixed(precision);
      const parts = fixed.split('.');
      return `${sign}${parts[0].padStart(safeDigitCount, '0')}.${parts[1]}`;
    }

    return `${sign}${String(Math.round(abs)).padStart(safeDigitCount, '0')}`;
  }

  function animateCounter(el) {
    if (!el || el.dataset.animated === 'true') return;

    const rawTarget = el.dataset.target || '0';
    const target = parseTarget(rawTarget);
    const precision = inferPrecision(rawTarget);
    const digitCount = Math.max(Number.parseInt(el.dataset.digitCount || '1', 10), 1);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.textContent = formatValue(target, precision, digitCount);
      el.dataset.animated = 'true';
      return;
    }

    const startTime = performance.now();
    const duration = 1200;

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = precision > 0 ? target * eased : Math.round(target * eased);

      el.textContent = formatValue(current, precision, digitCount);

      if (progress < 1) {
        window.requestAnimationFrame(tick);
      } else {
        el.textContent = formatValue(target, precision, digitCount);
        el.dataset.animated = 'true';
      }
    };

    window.requestAnimationFrame(tick);
  }

  function setInitialCounterValue(el) {
    if (!el || el.dataset.initialized === 'true') return;

    const rawTarget = el.dataset.target || '0';
    const precision = inferPrecision(rawTarget);
    const digitCount = Math.max(Number.parseInt(el.dataset.digitCount || '1', 10), 1);

    el.textContent = formatValue(0, precision, digitCount);
    el.dataset.initialized = 'true';
    el.dataset.animated = 'false';
  }

  function initStatCardAnimation(sectionRoot) {
    if (!sectionRoot || sectionRoot.dataset.statsAnimInitialized === 'true') return;

    const statsContainer = sectionRoot.querySelector(STATS_CONTAINER_SELECTOR);
    const statCards = statsContainer ? Array.from(statsContainer.querySelectorAll(STAT_CARD_SELECTOR)) : [];
    if (!statsContainer || !statCards.length) return;

    sectionRoot.dataset.statsAnimInitialized = 'true';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      statCards.forEach((card) => {
        card.style.opacity = '1';
        card.style.transform = 'none';
      });
      return;
    }

    const initGsapStats = () => {
      if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
        return false;
      }

      gsap.from(statCards, {
        y: 60,
        autoAlpha: 0,
        duration: 0.8,
        ease: 'cubic-bezier(.27, 1, .48, 1)',
        stagger: 0.12,
        force3D: true,
        clearProps: 'all',
        scrollTrigger: {
          trigger: statsContainer,
          start: 'top 82%',
          once: true,
        },
      });

      return true;
    };

    if (initGsapStats()) return;

    const onGsapReady = () => {
      sectionRoot.dataset.statsAnimInitialized = 'false';
      initStatCardAnimation(sectionRoot);
    };

    document.addEventListener('theme:gsap:ready', onGsapReady, { once: true });
  }

  function initButtonsAnimation(sectionRoot) {
    if (!sectionRoot || sectionRoot.dataset.buttonsAnimInitialized === 'true') return;

    const buttonsContainer = sectionRoot.querySelector(BUTTONS_CONTAINER_SELECTOR);
    const buttons = buttonsContainer ? Array.from(buttonsContainer.querySelectorAll(BUTTON_SELECTOR)) : [];
    if (!buttonsContainer || !buttons.length) return;

    sectionRoot.dataset.buttonsAnimInitialized = 'true';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      buttons.forEach((button) => {
        button.style.opacity = '1';
        button.style.transform = 'none';
      });
      return;
    }

    const initGsapButtons = () => {
      if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
        return false;
      }

      gsap.from(buttons, {
        y: 60,
        autoAlpha: 0,
        duration: 0.8,
        ease: 'cubic-bezier(.27, 1, .48, 1)',
        stagger: 0.12,
        force3D: true,
        clearProps: 'all',
        scrollTrigger: {
          trigger: buttonsContainer,
          start: 'top 88%',
          once: true,
        },
      });

      return true;
    };

    if (initGsapButtons()) return;

    const onGsapReady = () => {
      sectionRoot.dataset.buttonsAnimInitialized = 'false';
      initButtonsAnimation(sectionRoot);
    };

    document.addEventListener('theme:gsap:ready', onGsapReady, { once: true });
  }

  function initSection(sectionRoot) {
    if (!sectionRoot) return;

    initParallax(sectionRoot);
    initStatCardAnimation(sectionRoot);
    initButtonsAnimation(sectionRoot);

    const counters = Array.from(sectionRoot.querySelectorAll(VALUE_SELECTOR));
    if (!counters.length) return;

    counters.forEach(setInitialCounterValue);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -40px 0px',
        threshold: 0.35,
      }
    );

    counters.forEach((counter) => observer.observe(counter));
  }

  function initAll(root = document) {
    const sections = Array.from(root.querySelectorAll(SECTION_SELECTOR));
    sections.forEach(initSection);
  }

  // Guard against DOMContentLoaded already having fired (Theme Editor and cached-script scenarios)
  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
    document.addEventListener('shopify:section:unload', (event) => teardownParallax(event.target));
    document.addEventListener('shopify:section:reorder', () => initAll(document));
  }
})();
